import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import { generatePath, useNavigate } from 'react-router-dom';
import { HttpResponseList } from 'shell/models/http-response';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValuesGroups } from '..';
import { useFormik } from 'formik';
import { FiltersFormGroup, GroupClient } from './types';
import { transformDataset } from './utils';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { Group } from 'shell/models/group';
import FiltersGroup from 'system/components/groups/list/filters';
import { CommonGroupCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { authsObj, headCells, validationSchemaGroupsFilter } from './constants';
import ListHeader from 'shell/components/shared/list-header';
import { Typography } from '@mui/material';

const GroupsPage = (): React.ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<GroupClient>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<GroupClient>>({
    element: null,
    open: false,
  });

  const { delete: deleteGroup, error } = useHttpFetch(`${__MANAGEMENT_API_URL__}/gruppi`);

  const filterTable = useFilterTable<FiltersFormGroup>();

  const { data, get: getGroups } = useHttpFetch<HttpResponseList<Group>>(
    `${__MANAGEMENT_API_URL__}/gruppi`
  );

  const onSubmit = useCallback(
    (formValues: FiltersFormGroup) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterGroupForm.setFieldValue('page', 0);
      getGroups(queryString);
    },
    [getGroups]
  );

  const groups: Array<GroupClient> = useMemo(() => {
    if (!!error || !data?.payload) {
      return [];
    }
    return data.payload.list?.map((group) => ({
      description: group.nome,
      code: group.key.codice.toString(),
    }));
  }, [data, error]);

  const filterGroupForm = useFormik({
    initialValues: initialValuesGroups,
    validationSchema: validationSchemaGroupsFilter,
    onSubmit,
  });

  useEffect(() => {
    filterGroupForm.setValues(filterTable.values || initialValuesGroups);
    const queryString = transformDataset(filterTable.values || initialValuesGroups);
    getGroups(queryString);
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterGroupForm.values);
  }, [filterGroupForm.values]);

  const count: number = useMemo(() => {
    if (!!error || !data?.payload || !data?.payload?.list) {
      return 0;
    }
    return data.payload.list?.length;
  }, [error, data?.payload]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const body = useMemo(() => {
    if (!groups) {
      return [];
    }
    return groups.map((group) => ({ ...group }));
  }, [groups]);

  const onVisibilityPress = useCallback(
    (cell): Promise<GroupClient> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/gruppi/dettaglio/:id', { id: cell.code }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<GroupClient> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/gruppi/modifica/:id', { id: cell.code }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useCallback(
    (cell): Promise<GroupClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<GroupClient>;
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      filterGroupForm.setFieldValue('page', newPage);
    },
    [filterGroupForm.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = { ...filterGroupForm.values, rowsPerPage: rowsPerPage || 0, page: 0 };
      filterGroupForm.setValues(newDataset);
    },
    [filterGroupForm.setFieldValue, filterGroupForm.values]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterGroupForm.values, orders };
      const queryString = transformDataset(newDataset);
      return getGroups(queryString).then(() => {
        filterGroupForm.setFieldValue('orders', orders);
      });
    },
    [filterGroupForm.setFieldValue, getGroups, filterGroupForm.values]
  );

  return (
    <Fragment>
      <Breadcrumbs items={CommonGroupCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo gruppo"
        addBtnLabel="Aggiungi nuovo gruppo"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/gruppi/nuovo');
        }}
        title="Gestione Gruppi"
      />
      <div className="pmi-my-3">
        <FiltersGroup filterGroupForm={filterGroupForm} />
      </div>
      {data?.payload?.list ? (
        body?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterGroupForm.values.orders}
            activeActions={auths}
            page={filterGroupForm.values.page}
            rowsPerPage={filterGroupForm.values.rowsPerPage}
            body={body}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Gruppi"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Gruppo"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          deleteGroup(`/${deleteAlertModal.element?.code}`).then(() => {
            setDeleteAlertModal({ open: false, element: null });
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Gruppo"
        message={`La cancellazione del gruppo ${deleteSuccessModal.element?.description} è avvenuta con successo.`}
        onClose={() => {
          const queryString = transformDataset({ ...filterGroupForm.values, page: 0 });
          getGroups(queryString).then(() => setDeleteSuccessModal({ open: false, element: null }));
        }}
        onOk={() => {
          const queryString = transformDataset({ ...filterGroupForm.values, page: 0 });
          getGroups(queryString).then(() => setDeleteSuccessModal({ open: false, element: null }));
        }}
      />
    </Fragment>
  );
};

GroupsPage.displayName = 'Groups page component';

export default GroupsPage;
